import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { usePwaInstall } from '../../context/PwaInstallContext';
import { useScreen } from '../../context/ScreenContext';
import {
  dismissPwaInstallPrompt,
  isIosDevice,
  isPwaInstallPromptInCooldown,
  markPwaInstallPromptShown,
  wasPwaInstallPromptShownThisSession,
} from '../../utils/pwaInstall';
import * as Styled from './styles';

export const PWA_INSTALL_PROMPT_DELAY_MS = 1500;

const IokInstallLogo = () => (
  <Styled.Logo
    viewBox='50 0 186 195'
    aria-hidden='true'
    focusable='false'
    data-testid='pwa-install-logo'
  >
    <Styled.LogoAccent d='M51.44,36.33a2.27,2.27,0,0,0-.07,3.13L72.66,63a2.28,2.28,0,0,0,1.64.75h0A2.32,2.32,0,0,0,76,63.06,95.53,95.53,0,0,1,119.6,37.83a96,96,0,0,1,91.48,24,2.28,2.28,0,0,0,3.2,0l21.29-21.29a2.29,2.29,0,0,0,0-3.24A130,130,0,0,0,144.15,0C107.67,0,74.74,12.9,51.44,36.33Z' />
    <Styled.LogoAccent d='M143.58,46.92a69.94,69.94,0,0,0-17.21,2.22,86.29,86.29,0,0,0-39.58,23.1,2.29,2.29,0,0,0,0,3.19l18,18.5a2.26,2.26,0,0,0,1.62.69A2.47,2.47,0,0,0,108,94C118.36,83.5,127.92,77,143.48,77c15,0,25.19,6.94,35.93,17.38a2.34,2.34,0,0,0,1.59.64h.08a2.25,2.25,0,0,0,1.62-.75l17.6-19.72a2.26,2.26,0,0,0-.11-3.14A87.57,87.57,0,0,0,160.66,49.1,70.86,70.86,0,0,0,143.58,46.92Z' />
    <Styled.LogoBody d='M204.23,79.06l-17.1,19.16a8.25,8.25,0,0,1-4.15,2.51,42.76,42.76,0,0,1,4.63,19.75,42.15,42.15,0,0,1-13,31.2,44.18,44.18,0,0,1-62.32,0,44.27,44.27,0,0,1-8-51.57,8.12,8.12,0,0,1-3.72-2.06l-18-18.52a.56.56,0,0,1,0-.08A70.79,70.79,0,0,0,69.9,120.66q0,30.39,21.58,52,21.49,21.32,52,21.41t52-21.41q21.67-21.49,21.67-52A70.44,70.44,0,0,0,204.23,79.06Z' />
    <Styled.LogoAccent d='M121.72,120.43a21.79,21.79,0,1,0,21.79-21.79A21.82,21.82,0,0,0,121.72,120.43Z' />
  </Styled.Logo>
);

const PwaInstallPrompt = () => {
  const intl = useIntl();
  const { isMobile, screenSize } = useScreen();
  const { canPromptInstall, isInstalled, requestInstall } = usePwaInstall();
  const [isVisible, setIsVisible] = useState(false);
  const [showIosInstructions, setShowIosInstructions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const isIos = useMemo(isIosDevice, []);

  const closePrompt = useCallback((startCooldown: boolean) => {
    if (startCooldown) {
      dismissPwaInstallPrompt();
    }

    setIsVisible(false);
    setShowIosInstructions(false);
  }, []);

  useEffect(() => {
    if (
      screenSize.width === 0 ||
      !isMobile ||
      isInstalled ||
      (!canPromptInstall && !isIos) ||
      wasPwaInstallPromptShownThisSession() ||
      isPwaInstallPromptInCooldown()
    ) {
      return;
    }

    const timeout = window.setTimeout(() => {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      markPwaInstallPromptShown();
      setIsVisible(true);
    }, PWA_INSTALL_PROMPT_DELAY_MS);

    return () => window.clearTimeout(timeout);
  }, [canPromptInstall, isInstalled, isIos, isMobile, screenSize.width]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    dialogRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) {
        closePrompt(true);
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const focusableElements = Array.from(
        dialogRef.current?.querySelectorAll<HTMLButtonElement>(
          'button:not(:disabled)'
        ) ?? []
      );

      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (
        event.shiftKey &&
        (document.activeElement === firstElement ||
          document.activeElement === dialogRef.current)
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      const previousFocus = previousFocusRef.current;
      if (previousFocus?.isConnected) {
        previousFocus.focus();
      }
    };
  }, [closePrompt, isSubmitting, isVisible]);

  useEffect(() => {
    if (isInstalled) {
      closePrompt(false);
    }
  }, [closePrompt, isInstalled]);

  const handleInstall = async () => {
    if (isIos) {
      setShowIosInstructions(true);
      return;
    }

    try {
      setIsSubmitting(true);
      const outcome = await requestInstall();
      closePrompt(outcome === 'dismissed');
    } catch {
      closePrompt(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) {
    return null;
  }

  const titleId = 'pwa-install-title';
  const descriptionId = 'pwa-install-description';

  return (
    <Styled.Backdrop data-testid='pwa-install-backdrop'>
      <Styled.Dialog
        ref={dialogRef}
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
      >
        <Styled.Content>
          <Styled.Heading>
            <IokInstallLogo />
            <Styled.Title id={titleId}>
              {intl.formatMessage({
                id: showIosInstructions
                  ? 'pwaInstall.iosTitle'
                  : 'pwaInstall.title',
              })}
            </Styled.Title>
          </Styled.Heading>
          <Styled.Description id={descriptionId}>
            {intl.formatMessage({
              id: showIosInstructions
                ? 'pwaInstall.iosInstructions'
                : 'pwaInstall.description',
            })}
          </Styled.Description>

          {showIosInstructions ? (
            <>
              <Styled.IosHint aria-hidden='true'>
                <Styled.ShareSymbol />
              </Styled.IosHint>
              <Styled.Actions className='single-action'>
                <Styled.ActionButton
                  type='button'
                  data-variant='install'
                  onClick={() => closePrompt(true)}
                >
                  {intl.formatMessage({ id: 'pwaInstall.done' })}
                </Styled.ActionButton>
              </Styled.Actions>
            </>
          ) : (
            <Styled.Actions>
              <Styled.ActionButton
                type='button'
                data-variant='install'
                aria-busy={isSubmitting}
                disabled={isSubmitting}
                onClick={() => void handleInstall()}
              >
                {intl.formatMessage({ id: 'pwaInstall.install' })}
              </Styled.ActionButton>
              <Styled.ActionButton
                type='button'
                data-variant='dismiss'
                disabled={isSubmitting}
                onClick={() => closePrompt(true)}
              >
                {intl.formatMessage({ id: 'pwaInstall.notNow' })}
              </Styled.ActionButton>
            </Styled.Actions>
          )}
        </Styled.Content>
      </Styled.Dialog>
    </Styled.Backdrop>
  );
};

export default PwaInstallPrompt;

